import { dataLayerConfig } from '@aspendental/shared-components-web';
import { Box, Typography } from '@mui/material';
import { useTranslation } from 'next-i18next';
import { FC } from 'react';
import { useShallow } from 'zustand/react/shallow';

import ScheduleQuestionCheckbox from './ScheduleQuestionCheckbox';
import ScheduleQuestionRadioGroup from './ScheduleQuestionRadioGroup';

import useScheduleQuestionsStore from '@src/store/schedule-questions.store';

const ScheduleQuestion: FC<any> = ({ id, question_text, required, question_type, options, short_name, unanswered }) => {
	const answers = useScheduleQuestionsStore(useShallow((s) => s.questionsAnswers?.[short_name]));
	const setQuestionAnswer = useScheduleQuestionsStore((store) => store.setQuestionAnswer);
	const { t } = useTranslation('index');

	// Handle Checkbox answer selections
	const handleCheckboxChange = (optionShortName: string) => {
		let newAnswers = [...(answers || [])];
		if (optionShortName === 'none') {
			newAnswers = ['none'];
		} else {
			if (newAnswers.includes(optionShortName)) {
				newAnswers = newAnswers.filter((answer) => answer !== optionShortName);
			} else {
				newAnswers.push(optionShortName);
			}
			newAnswers = newAnswers.filter((answer) => answer !== 'none');
		}

		let event = '';
		if (short_name === 'current_dental_condition') {
			event = 'scheduleform_step1_dentalcondition';
		}
		if (short_name === 'current_dental_solutions') {
			event = 'scheduleform_step1_currentsolutions';
		}

		if (event) {
			dataLayerConfig({
				event,
				selections: newAnswers.join(', '),
			});
		}

		setQuestionAnswer(short_name, newAnswers);
	};

	// Handle Radio answer selections
	const handleRadioChange = (optionsShortName: string) => {
		setQuestionAnswer(short_name, optionsShortName);
		dataLayerConfig({
			event: `scheduleform_step1_${short_name}`,
			optionsShortName,
		});
	};

	return (
		<Box key={id} data-test-id="section_schedule_question" sx={{ mb: { md: 2 } }}>
			{/* Responsive Flexbox Container */}
			<Box
				sx={{
					display: 'flex',
					flexDirection: { md: 'row', xs: 'column' }, // Row layout on desktop, Column on mobile
					alignItems: 'flex-start', // Align content properly
				}}
			>
				{/* Left Section: Question Text */}
				<Box
					sx={{
						flex: { md: '0 0 40%', xs: '1' }, // Half the width on Desktop, full width on mobile
					}}
				>
					{/* Unanswered Warning */}
					{unanswered && (
						<Typography variant="bodySmallBook" sx={{ color: 'error.dark' }}>
							{t('required').toUpperCase()}
						</Typography>
					)}

					{/* Question Text */}
					<Typography
						variant="header4"
						color={unanswered ? 'error.dark' : 'text.primary'}
						id={short_name}
						mb={1}
					>
						{question_text}
						{(required === 1 || required === '1') && ' *'}
					</Typography>

					{/* Question Subtext */}
					{question_type === 'checkbox' && (
						<Typography
							variant="bodyLargeBook"
							color="text.primary"
							display="flex"
							width="80%"
							sx={{
								paddingBottom: { xs: '16px', md: '0px' },
							}}
						>
							{t('selectFromGroup')}
						</Typography>
					)}

					{question_type === 'radio' && (
						<Typography
							variant="bodyLargeBook"
							color="text.primary"
							display="flex"
							width="80%"
							sx={{
								paddingBottom: { xs: '16px', md: '0px' },
							}}
						>
							{t('selectOne')}
						</Typography>
					)}
				</Box>

				{/* Right Section: Answer Options */}
				<Box
					sx={{
						flex: { md: '0 0 40%', xs: '1' }, // Half the width on Desktop, full width on mobile
						display: 'flex',
						flexDirection: 'column',
						marginTop: { md: -2, xs: 0 },
						marginLeft: { md: 2 },
					}}
					aria-labelledby={short_name}
				>
					{/* Render Checkbox Group */}
					{question_type === 'checkbox' && (
						<ScheduleQuestionCheckbox
							options={options}
							answers={answers}
							short_name={short_name}
							handleChange={handleCheckboxChange}
						/>
					)}
					{/* Render Radio Group */}
					{question_type === 'radio' && (
						<ScheduleQuestionRadioGroup
							options={options}
							answer={answers}
							short_name={short_name}
							handleChange={handleRadioChange}
						/>
					)}
				</Box>
			</Box>
		</Box>
	);
};

export default ScheduleQuestion;
